import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaSearch } from 'react-icons/fa';
import { getApplicationDoctors, cancelVcOnBehalf } from '../services/doctorService';
import { useAuth } from '../auth/AuthContext';
import { formatDateTimeIST } from '../utils/dateFormatter';

const VirtualConsultationList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();

  // State for API data
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Status filter options from API (no longer used in UI)
  // const [statusOptions, setStatusOptions] = useState([]);

  // Search state - initialize from URL or empty
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const searchTimeoutRef = useRef(null);

  // Date and status filters are no longer needed in this module.
  // The related state and helpers are commented out to keep the logic simple.
  //
  // const [dateFilter, setDateFilter] = useState(() => searchParams.get('date_filter') || '');
  // const [startDate, setStartDate] = useState(() => searchParams.get('start_date') || '');
  // const [endDate, setEndDate] = useState(() => searchParams.get('end_date') || '');
  //
  // const startDateObj = startDate ? new Date(startDate) : null;
  // const endDateObj = endDate ? new Date(endDate) : null;
  //
  // const dateToString = (date) => {
  //   if (!date) return '';
  //   const year = date.getFullYear();
  //   const month = String(date.getMonth() + 1).padStart(2, '0');
  //   const day = String(date.getDate()).padStart(2, '0');
  //   return `${year}-${month}-${day}`;
  // };
  //
  // const CustomDateInput = React.forwardRef(({ value, onClick, placeholder, clearDate }, ref) => (
  //   <div className="relative w-full">
  //     <button
  //       type="button"
  //       onClick={onClick}
  //       ref={ref}
  //       className="w-full px-3 py-1.5 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm text-left hover:border-gray-400 transition-colors"
  //     >
  //       <span className={value ? 'text-gray-900 block truncate pr-8' : 'text-gray-400 block truncate pr-8'}>
  //         {value || placeholder}
  //       </span>
  //     </button>
  //     <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
  //       {value && clearDate && (
  //         <button
  //           type="button"
  //           onClick={(e) => {
  //             e.stopPropagation();
  //             clearDate();
  //           }}
  //           className="text-gray-400 hover:text-gray-600 p-0.5 rounded hover:bg-gray-100 pointer-events-auto"
  //           aria-label="Clear date"
  //         >
  //           <FaTimes className="w-3 h-3" />
  //         </button>
  //       )}
  //       <FaCalendarAlt className="text-gray-400 w-4 h-4 pointer-events-none" />
  //     </div>
  //   </div>
  // ));
  // CustomDateInput.displayName = 'CustomDateInput';
  //
  // const [selectedStatuses, setSelectedStatuses] = useState(() => {
  //   const statusParam = searchParams.get('status');
  //   return statusParam ? statusParam.split(',') : [];
  // });
  //
  // const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  // Auto-detect role from authentication
  const currentRole = (() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      'wallet_superadmin': 'SuperAdmin',
      'wallet_finance': 'Finance',
      'wallet_ops': 'Ops',
      'wallet_support': 'Support'
    };
    return roleMapping[authRole] || null;
  })();

  const PAGE_SIZE = 25;
  // Initialize page from URL or default to 1
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [pagination, setPagination] = useState({
    page: 1,
    items: PAGE_SIZE,
    total: 0,
    total_pages: 0
  });

  const isOpsOrSuperAdmin = currentRole === 'Ops' || currentRole === 'SuperAdmin';

  // Track which row is being acted on — separate trackers so only the clicked
  // button disables, not both.
  const [rescheduleRowId, setRescheduleRowId] = useState(null);
  const [cancelRowId, setCancelRowId] = useState(null);
  const [rowError, setRowError] = useState(null);

  const handleCancel = useCallback(async (item) => {
    if (!item?.id || !item?.application) return;
    if (item.can_cancel === false) return;
    const confirmed = window.confirm(
      `Cancel this consultation for ${item?.member_data?.name || 'patient'}?\nThe subscriber and doctor will be notified.`
    );
    if (!confirmed) return;
    setCancelRowId(item.id);
    setRowError(null);
    try {
      await cancelVcOnBehalf(currentRole, item.application, item.id);
      // Optimistic: reflect status locally without another round trip.
      // Only mark can_cancel false — rescheduling is independent.
      setConsultations((prev) =>
        prev.map((c) => (c.id === item.id ? { ...c, status: 'cancelled', can_cancel: false } : c))
      );
    } catch (err) {
      setRowError(err?.message || 'Failed to cancel consultation');
    } finally {
      setCancelRowId(null);
    }
  }, [currentRole]);

  const handleReschedule = useCallback(async (item) => {
    if (!item?.application) return;
    if (item.can_reschedule === false) return;
    const confirmed = window.confirm(
      `Reschedule this consultation for ${item?.member_data?.name || 'patient'}?\nThe current slot will be cancelled and the subscriber can book a new one.`
    );
    if (!confirmed) return;
    setRescheduleRowId(item.id);
    setRowError(null);
    try {
      await cancelVcOnBehalf(currentRole, item.application, item.id, { asReschedule: true });
      setConsultations((prev) =>
        prev.map((c) => (c.id === item.id ? { ...c, status: 'rescheduled', can_reschedule: false, can_cancel: false } : c))
      );
    } catch (err) {
      setRowError(err?.message || 'Failed to reschedule (cancel step failed)');
    } finally {
      setRescheduleRowId(null);
    }
  }, [currentRole]);
  // For Ops and SuperAdmin: start with masked, can toggle. For Support: always masked (no toggle)
  const [mobilesUnmasked, setMobilesUnmasked] = useState(false);

  // Track if initial mount is complete to prevent duplicate calls
  const isInitialMountRef = useRef(true);
  // Track last API call parameters to prevent duplicate calls
  const lastApiCallRef = useRef({});
  // Track if API call is in progress to prevent concurrent calls
  const isLoadingRef = useRef(false);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (page > 1) params.set('page', page.toString());
    if (search.trim()) params.set('search', search.trim());

    setSearchParams(params, { replace: true });
  }, [page, search, setSearchParams]);

  // Handle search submission (Enter key or debounce)
  const handleSearchSubmit = useCallback(() => {
    const newSearch = searchInput.trim();
    if (newSearch !== search) {
      setSearch(newSearch);
      setPage(1); // Reset to page 1 when search changes
    }
    // Clear any pending timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  }, [searchInput, search]);

  // Debounce search input (2 seconds)
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      handleSearchSubmit();
    }, 2000);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput, handleSearchSubmit]);

  const maskMobile = (value) => {
    if (!value) return 'N/A';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length <= 4) return `******${digits}`;
    const last4 = digits.slice(-4);
    return `******${last4}`;
  };

  // Helper function to format date from ISO string (e.g., "2026-01-24T13:30:00+05:30")
  const formatAppointmentDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const day = String(date.getDate()).padStart(2, '0');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  // Helper function to format time from ISO string (e.g., "2026-01-24T13:30:00+05:30")
  const formatAppointmentTime = (startAt, endAt) => {
    if (!startAt) return 'N/A';
    try {
      const startDate = new Date(startAt);
      if (isNaN(startDate.getTime())) return 'N/A';

      let hours = startDate.getHours();
      let minutes = startDate.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      const startTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${ampm}`;

      if (endAt) {
        const endDate = new Date(endAt);
        if (!isNaN(endDate.getTime())) {
          let endHours = endDate.getHours();
          let endMinutes = endDate.getMinutes();
          const endAmpm = endHours >= 12 ? 'PM' : 'AM';
          endHours = endHours % 12;
          endHours = endHours ? endHours : 12;
          const endTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')} ${endAmpm}`;
          return `${startTime} - ${endTime}`;
        }
      }

      return startTime;
    } catch (error) {
      return 'N/A';
    }
  };

  // Helper function to format symptoms
  const formatSymptoms = (symptoms, symptomsList) => {
    // symptoms_list is an array of objects with { id, name }
    if (symptomsList && Array.isArray(symptomsList) && symptomsList.length > 0) {
      // Extract names from objects
      const symptomNames = symptomsList
        .map(symptom => {
          if (typeof symptom === 'object' && symptom !== null) {
            return symptom.name || symptom.id;
          }
          return symptom;
        })
        .filter(name => name); // Remove any null/undefined values

      return symptomNames.length > 0 ? symptomNames.join(', ') : 'N/A';
    }
    // Fallback to symptoms array (if it's just IDs)
    if (symptoms && Array.isArray(symptoms) && symptoms.length > 0) {
      return symptoms.join(', ');
    }
    return 'N/A';
  };

  // Load virtual consultations from API
  useEffect(() => {
    // Skip on initial mount if auth is still loading
    if (authLoading) {
      return;
    }

    // Skip if not authenticated or no role
    if (!isAuthenticated || !currentRole) {
      return;
    }

    // Only allow Ops and SuperAdmin
    if (currentRole !== 'Ops' && currentRole !== 'SuperAdmin') {
      return;
    }

    // Build API params
    const apiParams = {
      page: page || 1,
      items: PAGE_SIZE
    };

    if (search.trim()) apiParams.search = search.trim();
    // Date and status filters are disabled for this module,
    // so we no longer send those params to the API.

    // Create a key for comparison
    const apiCallKey = JSON.stringify(apiParams);

    // Prevent duplicate API calls with same parameters
    if (lastApiCallRef.current.key === apiCallKey) {
      return;
    }

    // Prevent concurrent API calls
    if (isLoadingRef.current) {
      return;
    }

    // Update last API call reference
    lastApiCallRef.current = { key: apiCallKey, params: apiParams };

    const loadConsultations = async () => {
      isLoadingRef.current = true;
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('authToken');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await getApplicationDoctors(currentRole, apiParams);

        let consultationsData = null;
        let paginationData = null;

        // Handle response structure: { msg: { items: [...], pagination: {...}, filters: {...} }, metadata: {...} }
        if (response && response.msg) {
          if (response.msg.items && Array.isArray(response.msg.items)) {
            consultationsData = response.msg.items;
          }
          if (response.msg.pagination) {
            paginationData = response.msg.pagination;
          }
          // Filter options from API (status, etc.) are no longer needed here.
        } else if (response && response.data) {
          if (response.data.items && Array.isArray(response.data.items)) {
            consultationsData = response.data.items;
          }
          if (response.data.pagination) {
            paginationData = response.data.pagination;
          }
          // Filter options from API (status, etc.) are no longer needed here.
        } else if (Array.isArray(response)) {
          consultationsData = response;
          paginationData = {
            page: 1,
            items: response.length,
            total: response.length,
            total_pages: Math.ceil(response.length / PAGE_SIZE)
          };
        }

        if (consultationsData && Array.isArray(consultationsData)) {
          setConsultations(consultationsData);
          if (paginationData) {
            // Calculate total_pages from total and items (backend doesn't provide total_pages)
            const calculatedTotalPages = Math.ceil(paginationData.total / paginationData.items);
            setPagination({
              ...paginationData,
              total_pages: calculatedTotalPages
            });
          } else {
            setPagination({
              page: page || 1,
              items: PAGE_SIZE,
              total: consultationsData.length,
              total_pages: Math.ceil(consultationsData.length / PAGE_SIZE)
            });
          }
        } else {
          setConsultations([]);
          setPagination({
            page: 1,
            items: PAGE_SIZE,
            total: 0,
            total_pages: 0
          });
          setError(`Failed to load virtual consultations - Response: ${JSON.stringify(response).substring(0, 200)}...`);
        }
      } catch (error) {
        console.error('Error loading virtual consultations:', error);
        setError('Failed to load virtual consultations');
        setConsultations([]);
        setPagination({
          page: 1,
          items: PAGE_SIZE,
          total: 0,
          total_pages: 0
        });
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
        if (isInitialMountRef.current) {
          isInitialMountRef.current = false;
        }
      }
    };

    loadConsultations();
  }, [currentRole, isAuthenticated, authLoading, navigate, page, search]);

  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access the Subscriber Wallet.</p>
          <button
            onClick={() => navigate('/subscriber-wallet')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // Check if user has access (Ops and SuperAdmin only)
  if (currentRole !== 'Ops' && currentRole !== 'SuperAdmin') {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access Virtual Consultation.</p>
          <button
            onClick={() => navigate('/subscriber-wallet')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (loading && consultations.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && consultations.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/subscriber-wallet')}
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Home
          </button>
        </div>

        <div className="bg-white rounded-md shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <span className="mr-2">Virtual Consultation</span>
              </h2>
            </div>

            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Patient Name, Mobile, Doctor Name, or REF CODE..."
                  className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearchSubmit();
                    }
                  }}
                />
              </div>
            </div>

            {/* Filters (status & date) are not needed in this module anymore. */}
            {/*
            <div className="space-y-4">
              ... (date and status filter UI was here)
            </div>
            */}
          </div>

          {rowError && (
            <div className="mx-6 mt-3 px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md flex items-center justify-between">
              <span>{rowError}</span>
              <button onClick={() => setRowError(null)} className="text-red-700 hover:text-red-900 font-semibold">✕</button>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ref Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <span>Mobile</span>
                      {isOpsOrSuperAdmin && (
                        <button
                          type="button"
                          onClick={() => setMobilesUnmasked((v) => !v)}
                          className="inline-flex items-center px-2 py-1 text-xs border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
                          title={mobilesUnmasked ? 'Mask all mobile numbers' : 'Unmask all mobile numbers'}
                        >
                          {mobilesUnmasked ? <FaEyeSlash className="w-3 h-3" /> : <FaEye className="w-3 h-3" />}
                        </button>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Doctor Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appointment Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appointment Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Symptoms</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {consultations.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                      {loading ? 'Loading...' : 'No virtual consultations found'}
                    </td>
                  </tr>
                ) : (
                  consultations.map((item) => {
                    // Extract patient data from member_data
                    const patientName = item.member_data?.name || item.subscriber_name || item.name || 'N/A';
                    const patientMobile = item.member_data?.contact_number || item.mobile_number || item.mobile || '';
                    const mobileDisplay = !mobilesUnmasked
                      ? maskMobile(patientMobile)
                      : (patientMobile || 'N/A');

                    // Extract doctor name from doctor_data
                    const doctorName = item.doctor_data
                      ? `${item.doctor_data.first_name || ''} ${item.doctor_data.last_name || ''}`.trim() || item.doctor_name || 'N/A'
                      : (item.doctor_name || 'N/A');

                    // Format appointment date and time from start_at and end_at
                    const appointmentDate = formatAppointmentDate(item.start_at);
                    const appointmentTime = formatAppointmentTime(item.start_at, item.end_at);

                    // Format symptoms - extract names from symptoms_list objects
                    const symptoms = formatSymptoms(item.symptoms, item.symptoms_list);

                    // Format status (handle different case variations)
                    const status = item.status || 'N/A';
                    const statusLower = String(status).toLowerCase();

                    return (
                      <tr
                        key={item.id || item.consultation_id}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.created_at ? formatDateTimeIST(item.created_at) : 'N/A'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusLower === 'confirmed' || statusLower === 'scheduled' ? 'bg-green-100 text-green-800' :
                            statusLower === 'cancelled' ? 'bg-red-100 text-red-800' :
                              statusLower === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                statusLower === 'completed' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                            }`}>
                            {status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.ref_code}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {patientName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {mobileDisplay}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {doctorName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {appointmentDate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {appointmentTime}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="max-w-xs truncate" title={symptoms}>
                            {symptoms}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.source === 'ops' ? (
                            <span
                              className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800"
                              title={item.created_by_name ? `Booked by ${item.created_by_name}` : 'Booked by Ops'}
                            >
                              Booked by Ops
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                              Self-booked
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {(statusLower === 'pending' || statusLower === 'confirmed') && isOpsOrSuperAdmin ? (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleReschedule(item)}
                                disabled={rescheduleRowId === item.id || item.can_reschedule === false}
                                title={item.can_reschedule === false ? 'Already rescheduled in the last 2 days' : 'Reschedule'}
                                className="px-2 py-1 text-xs border border-purple-600 text-purple-700 rounded hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {rescheduleRowId === item.id ? 'Rescheduling…' : 'Reschedule'}
                              </button>
                              <button
                                onClick={() => handleCancel(item)}
                                disabled={cancelRowId === item.id || item.can_cancel === false}
                                title={item.can_cancel === false ? 'Already cancelled in the last 2 days' : 'Cancel'}
                                className="px-2 py-1 text-xs border border-red-600 text-red-700 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {cancelRowId === item.id ? 'Cancelling…' : 'Cancel'}
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {((pagination.page - 1) * pagination.items) + 1}-{Math.min(pagination.page * pagination.items, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  disabled={pagination.page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page === 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                >
                  Prev
                </button>
                {Array.from({ length: pagination.total_pages || 1 }).map((_, i) => {
                  const pageNum = i + 1;
                  const totalPages = pagination.total_pages || 1;
                  const shouldShow =
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2);

                  if (!shouldShow) {
                    if (pageNum === pagination.page - 3 || pageNum === pagination.page + 3) {
                      return (
                        <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                          ...
                        </span>
                      );
                    }
                    return null;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-1 border rounded-md text-sm ${pagination.page === pageNum ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  disabled={pagination.page >= (pagination.total_pages || 1)}
                  onClick={() => setPage((p) => Math.min(pagination.total_pages || 1, p + 1))}
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page >= (pagination.total_pages || 1) ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VirtualConsultationList;

